import { useEffect, useRef, useState } from "react";

import type { ReadingReviewOut, Resolution } from "@/api";

/**
 * How long the screen waits after the last resolution before it asks the
 * server again (plan 4 §5: „nach jeder Auflösung dryRun“). Long enough that a
 * person setting three walls in a row sends one question, short enough that
 * „Mit deinem Wert schließt der Umlauf“ arrives while they still look.
 */
export const DRY_RUN_DEBOUNCE_MS = 250;

function keyOf(resolutions: Resolution[]): string {
  return JSON.stringify([...resolutions].sort((a, b) => a.wallId.localeCompare(b.wallId)));
}

/**
 * The review as the server would check it with these resolutions — without
 * writing anything (`take-over` with `dryRun: true`).
 *
 * Calm by construction: while a question is in flight the previous answer
 * stays on screen, nothing shows a spinner, and only the answer to the
 * *latest* question is ever taken — one that arrives late for a list
 * somebody has already changed again is dropped. With no resolutions there is
 * nothing to ask, and the reading itself is the answer.
 */
export function useDryRun(
  initial: ReadingReviewOut,
  resolutions: Resolution[],
  ask: (resolutions: Resolution[]) => Promise<ReadingReviewOut>,
): { current: ReadingReviewOut; error: unknown } {
  const key = keyOf(resolutions);
  const [answer, setAnswer] = useState<{ key: string; review: ReadingReviewOut } | null>(null);
  const [failure, setFailure] = useState<{ key: string; error: unknown } | null>(null);
  const ticket = useRef(0);

  useEffect(() => {
    const list = JSON.parse(key) as Resolution[];
    if (list.length === 0) return;
    const mine = ++ticket.current;
    const timer = window.setTimeout(() => {
      ask(list).then(
        (review) => {
          if (mine !== ticket.current) return;
          setAnswer({ key, review });
          setFailure(null);
        },
        (error: unknown) => {
          if (mine === ticket.current) setFailure({ key, error });
        },
      );
    }, DRY_RUN_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [key, ask]);

  return {
    current: resolutions.length === 0 ? initial : (answer?.review ?? initial),
    error: failure !== null && failure.key === key ? failure.error : null,
  };
}
